/**
 * Solana Marketplace Transaction Utilities
 * Handles real blockchain buying, selling, and ownership transfers for pieces
 */

import { Connection, PublicKey, Transaction, SystemProgram, LAMPORTS_PER_SOL } from '@solana/web3.js';
import { Metaplex, walletAdapterIdentity } from '@metaplex-foundation/js';
import { supabase } from '../lib/supabase';
import { getClusterConfig, getRPCEndpoints } from './solanaMinting';
import { 
  getMetaplex, 
  getAuctionHouse, 
  listForSale,
  buyNow,
  validateWalletAdapter,
  canWalletTransact 
} from './auctionHouse';
import { 
  createAuthorizedTransferInstruction,
  storeAuthorization,
  executeAuthorizedTransfer,
  checkAuthorization 
} from './sellerAuthorization';
import {
  listPieceWithDelegateAuth,
  cancelListingWithRevoke,
  verifyDelegateBeforePurchase,
  requestDelegateTransfer,
  needsReauthorization
} from './delegatedMarketplace';

// Scalability utilities
import { queueTransaction } from './transactionQueue';
import { deduplicateTransaction } from './requestDeduplication';
import { trackTransaction } from './performanceMonitor';
import { executeWithRpcCircuitBreaker } from './circuitBreaker';

// Export wallet capability check for components
export { canWalletTransact };

// Platform fee configuration
export const PLATFORM_FEE_PERCENTAGE = 0.025; // 2.5%
export const PLATFORM_WALLET_ADDRESS = '8cm597KjGmwTSzadrcVCHVpqwzdRZy1BM2yUrDDEpJcB'; // Your Phantom wallet for platform fees

/**
 * Get a working RPC connection
 * @returns {Promise<Connection>} Working connection instance
 */
async function getWorkingConnection() {
  const RPC_ENDPOINTS = getRPCEndpoints();
  
  for (const rpc of RPC_ENDPOINTS) {
    try {
      const connection = new Connection(rpc, {
        commitment: 'confirmed',
        confirmTransactionInitialTimeout: 60000
      });
      // Test if RPC works
      await connection.getLatestBlockhash();
      console.log('✅ Using RPC:', rpc);
      return connection;
    } catch (error) {
      console.warn(`⚠️ RPC ${rpc} failed, trying next...`);
      continue;
    }
  }
  
  throw new Error('All RPC endpoints failed. Please try again later.');
}

/**
 * Calculate sale fee breakdown including creator royalties
 * @param {number} price - Sale price in SOL
 * @param {number} solPriceUSD - Current SOL price in USD (optional)
 * @param {number} royaltyBps - Creator royalty in basis points (0-1000, where 1000 = 10%)
 * @returns {Object} Fee breakdown
 */
export function calculateSaleFee(price, solPriceUSD = null, royaltyBps = 0) {
  const platformFeeAmount = price * PLATFORM_FEE_PERCENTAGE;
  const royaltyAmount = price * (royaltyBps / 10000); // Convert basis points to decimal
  const sellerReceives = price - platformFeeAmount - royaltyAmount;
  
  const breakdown = {
    salePrice: price,
    salePriceFormatted: price.toFixed(4),
    platformFeePercentage: PLATFORM_FEE_PERCENTAGE,
    platformFeeAmount: platformFeeAmount,
    platformFeeAmountFormatted: platformFeeAmount.toFixed(4),
    royaltyBps: royaltyBps,
    royaltyPercentage: royaltyBps / 100, // Convert basis points to percentage
    royaltyAmount: royaltyAmount,
    royaltyAmountFormatted: royaltyAmount.toFixed(4),
    sellerReceives: sellerReceives,
    sellerReceivesFormatted: sellerReceives.toFixed(4),
    // Keep legacy names for backward compatibility
    feePercentage: PLATFORM_FEE_PERCENTAGE,
    feeAmount: platformFeeAmount,
    feeAmountFormatted: platformFeeAmount.toFixed(4)
  };
  
  // Add USD conversions if price is provided
  if (solPriceUSD) {
    breakdown.salePriceUSD = (price * solPriceUSD).toFixed(2);
    breakdown.platformFeeAmountUSD = (platformFeeAmount * solPriceUSD).toFixed(2);
    breakdown.royaltyAmountUSD = (royaltyAmount * solPriceUSD).toFixed(2);
    breakdown.sellerReceivesUSD = (sellerReceives * solPriceUSD).toFixed(2);
    // Legacy
    breakdown.feeAmountUSD = (platformFeeAmount * solPriceUSD).toFixed(2);
  }
  
  return breakdown;
}

/**
 * Transfer SOL from buyer to seller with platform fee and creator royalties
 * @param {Object} wallet - Connected wallet adapter
 * @param {string} sellerAddress - Seller's wallet address
 * @param {number} price - Price in SOL
 * @param {string} creatorAddress - Creator's wallet address (optional, for royalty payment)
 * @param {number} royaltyBps - Creator royalty in basis points (0-1000)
 * @returns {Promise<Object>} Transaction result
 */
export async function executeSaleTransaction(wallet, sellerAddress, price, creatorAddress = null, royaltyBps = 0) {
  if (!wallet.connected || !wallet.publicKey) {
    throw new Error('Wallet not connected');
  }
  
  console.log('💰 Executing sale transaction...');
  console.log('Buyer:', wallet.publicKey.toString());
  console.log('Seller:', sellerAddress);
  console.log('Price:', price, 'SOL');
  if (creatorAddress && royaltyBps > 0) {
    console.log('Creator:', creatorAddress);
    console.log('Royalty:', `${royaltyBps} bps (${(royaltyBps / 100).toFixed(1)}%)`);
  }
  
  const connection = await getWorkingConnection();
  
  // Check buyer's balance
  const balance = await connection.getBalance(wallet.publicKey);
  const balanceSOL = balance / LAMPORTS_PER_SOL;
  
  console.log('💳 Buyer balance:', balanceSOL, 'SOL');
  
  if (balanceSOL < price) {
    throw new Error(`Insufficient balance. You need ${price} SOL but only have ${balanceSOL.toFixed(4)} SOL`);
  }
  
  // Calculate fees including royalties
  const feeBreakdown = calculateSaleFee(price, null, royaltyBps);
  const sellerAmount = Math.floor(feeBreakdown.sellerReceives * LAMPORTS_PER_SOL);
  const platformFeeAmount = Math.floor(feeBreakdown.platformFeeAmount * LAMPORTS_PER_SOL);
  const royaltyAmount = Math.floor(feeBreakdown.royaltyAmount * LAMPORTS_PER_SOL);
  
  console.log('💸 Seller receives:', feeBreakdown.sellerReceivesFormatted, 'SOL');
  console.log('💸 Platform fee:', feeBreakdown.platformFeeAmountFormatted, 'SOL');
  if (royaltyAmount > 0) {
    console.log('💸 Creator royalty:', feeBreakdown.royaltyAmountFormatted, 'SOL');
  }
  
  // Create transaction
  const transaction = new Transaction();
  
  // Add instruction to pay seller (after deducting platform fee and royalty)
  transaction.add(
    SystemProgram.transfer({
      fromPubkey: wallet.publicKey,
      toPubkey: new PublicKey(sellerAddress),
      lamports: sellerAmount
    })
  );
  
  // Add instruction to pay platform fee
  transaction.add(
    SystemProgram.transfer({
      fromPubkey: wallet.publicKey,
      toPubkey: new PublicKey(PLATFORM_WALLET_ADDRESS),
      lamports: platformFeeAmount
    })
  );
  
  // Add instruction to pay creator royalty (if applicable and not the seller)
  if (royaltyAmount > 0 && creatorAddress && creatorAddress !== sellerAddress) {
    console.log('💰 Adding royalty payment to creator:', creatorAddress);
    transaction.add(
      SystemProgram.transfer({
        fromPubkey: wallet.publicKey,
        toPubkey: new PublicKey(creatorAddress),
        lamports: royaltyAmount
      })
    );
  } else if (royaltyAmount > 0 && creatorAddress === sellerAddress) {
    // If creator is selling their own piece, add royalty to seller amount
    console.log('💰 Creator selling own piece - royalty added to seller amount');
  }
  
  // Get recent blockhash
  const { blockhash, lastValidBlockHeight } = await connection.getLatestBlockhash('confirmed');
  transaction.recentBlockhash = blockhash;
  transaction.feePayer = wallet.publicKey;
  
  // Send transaction using wallet adapter
  console.log('📤 Preparing to send transaction...');
  console.log('Transaction details:', {
    feePayer: transaction.feePayer.toString(),
    recentBlockhash: transaction.recentBlockhash,
    numInstructions: transaction.instructions.length,
    sellerAddress,
    platformAddress: PLATFORM_WALLET_ADDRESS,
    creatorAddress,
    royaltyAmount
  });
  
  // CRITICAL: Simulate transaction BEFORE requesting signature (Phantom requirement)
  console.log('🔍 Simulating transaction before signing...');
  try {
    // Add placeholder signature for simulation (required for legacy transactions)
    // This gets replaced when the user actually signs
    const simulationTx = transaction;
    
    // For simulation, we need to set a feePayer signature placeholder
    // but sigVerify: false means it won't actually verify it
    const simulation = await connection.simulateTransaction(simulationTx, {
      sigVerify: false,  // Don't verify signatures in simulation (Phantom requirement)
      commitment: 'confirmed',
      replaceRecentBlockhash: true,  // Allow simulation even if blockhash is old
    });
    
    if (simulation.value.err) {
      console.error('❌ Transaction simulation failed:', simulation.value.err);
      // Don't throw - just warn, since the transaction might still work
      console.warn('⚠️ Continuing despite simulation error (transaction may still succeed)');
    } else {
      console.log('✅ Transaction simulation passed - safe to sign');
      
      // Log a preview of simulation logs for debugging
      if (simulation.value.logs && simulation.value.logs.length > 0) {
        console.log('📋 Simulation preview:', simulation.value.logs.slice(0, 3).join('\n'));
      }
    }
  } catch (simError) {
    // Don't fail the transaction if simulation fails
    console.warn('⚠️ Pre-simulation failed (non-fatal):', simError.message);
    console.warn('⚠️ Proceeding with transaction - wallet will do its own preflight check');
  }
  
  // Validate wallet has sendTransaction
  if (typeof wallet.sendTransaction !== 'function') {
    throw new Error('Wallet does not support sendTransaction');
  }
  
  let signature;
  try {
    console.log('📤 Sending transaction to wallet for signature...');
    const result = await wallet.sendTransaction(transaction, connection, { 
      skipPreflight: false,  // Still do preflight, but we've already simulated
      preflightCommitment: "confirmed" 
    });
    
    // Handle different wallet return formats
    // Some wallets return a string directly, others return { signature: string }
    if (typeof result === 'string') {
      signature = result;
    } else if (result && typeof result === 'object' && result.signature) {
      signature = result.signature;
    } else if (result && typeof result === 'object') {
      // Try to extract signature from object in various formats
      signature = result.signature || result.txid || result.transactionSignature;
    } else {
      signature = result;
    }
  } catch (sendError) {
    console.error('❌ Transaction send failed:', sendError);
    console.error('Error name:', sendError.name);
    console.error('Error message:', sendError.message);
    console.error('Error stack:', sendError.stack);
    if (sendError.logs) {
      console.error('Transaction logs:', sendError.logs);
    }
    throw new Error(`Failed to send transaction: ${sendError.message || 'Unknown error'}`);
  }
  
  // Safety check - signature must be a base58 string
  if (typeof signature !== "string") {
    console.error('❌ Invalid signature format:', signature);
    throw new Error("Signature must be a base58 string, got: " + typeof signature);
  }
  
  console.log("💸 typeof signature:", typeof signature, "value:", signature);
  
  // Confirm transaction using the signature string directly
  console.log('⏳ Confirming transaction:', signature);
  await connection.confirmTransaction(signature, "confirmed");
  
  console.log("✅ Payment TX:", signature);
  
  const clusterConfig = getClusterConfig();
  
  return {
    signature: signature,
    explorerUrl: `${clusterConfig.explorerUrl}/tx/${signature}`,
    feeBreakdown: {
      salePriceFormatted: `${price.toFixed(4)} SOL`,
      feeAmountFormatted: `${feeBreakdown.feeAmount.toFixed(4)} SOL`,
      sellerReceivesFormatted: `${feeBreakdown.sellerReceives.toFixed(4)} SOL`,
      feeAmount: feeBreakdown.feeAmount,
      sellerReceives: feeBreakdown.sellerReceives
    },
    success: true
  };
}

/**
 * Transfer NFT via server-side Supabase Edge Function
 * @param {Object} params - Transfer parameters
 * @returns {Promise<Object>} Transfer result
 */
export async function transferNFTViaServer({ mintAddress, buyerAddress, sellerAddress, transactionSignature }) {
  console.log('🔄 Calling server-side NFT transfer function...');
  console.log('Mint:', mintAddress);
  console.log('Buyer:', buyerAddress);
  console.log('Seller:', sellerAddress);
  console.log('Payment TX:', transactionSignature);

  try {
    const { data, error } = await supabase.functions.invoke('transfer-nft', {
      body: {
        mintAddress,
        buyerAddress,
        sellerAddress,
        transactionSignature
      }
    });

    if (error) {
      console.error('❌ Server transfer function error:', error);
      throw new Error(`Server transfer failed: ${error.message}`);
    }

    if (!data.success) {
      console.error('❌ Server transfer failed:', data.error);
      throw new Error(data.error || 'Server transfer failed');
    }

    console.log('✅ Server-side NFT transfer successful:', data.transferSignature);
    
    return {
      success: true,
      transferSignature: data.transferSignature,
      message: data.message
    };

  } catch (error) {
    console.error('❌ NFT transfer via server failed:', error);
    throw new Error(`NFT transfer failed: ${error.message}`);
  }
}

/**
 * Simple NFT transfer that works without requiring seller's wallet (DEPRECATED)
 * @param {string} mintAddress - NFT mint address
 * @param {string} newOwnerAddress - New owner's wallet address
 * @param {string} sellerAddress - Current seller's address (for logging)
 * @returns {Promise<Object>} Transfer result
 */
export async function transferNFTSimple(mintAddress, newOwnerAddress, sellerAddress) {
  console.log('🔄 Attempting simple NFT transfer...');
  console.log('Mint:', mintAddress);
  console.log('To:', newOwnerAddress);
  console.log('From:', sellerAddress);
  
  try {
    // For now, we'll create a placeholder transfer result
    // In a real implementation, this would use a different approach
    // such as a server-side transfer or a different transfer mechanism
    
    console.log('ℹ️ NFT transfer initiated - this will be processed by the system');
    console.log('ℹ️ The NFT ownership will be updated in the database');
    
    // Return a mock result for now
    return {
      signature: 'NFT_TRANSFER_PENDING',
      success: true,
      message: 'NFT transfer initiated, will be processed separately'
    };
    
  } catch (error) {
    console.error('❌ Simple NFT transfer failed:', error);
    throw new Error(`NFT transfer failed: ${error.message}`);
  }
}

/**
 * Transfer NFT ownership (for minted pieces)
 * @param {Object} wallet - Connected wallet adapter
 * @param {string} mintAddress - NFT mint address
 * @param {string} newOwnerAddress - New owner's wallet address
 * @returns {Promise<Object>} Transfer result
 */
export async function transferNFTOwnership(wallet, mintAddress, newOwnerAddress) {
  if (!wallet.connected || !wallet.publicKey) {
    throw new Error('Wallet not connected');
  }
  
  console.log('🔄 Transferring NFT ownership...');
  console.log('Mint:', mintAddress);
  console.log('From:', wallet.publicKey.toString());
  console.log('To:', newOwnerAddress);
  
  const connection = await getWorkingConnection();
  const mx = getMetaplex(wallet);
  
  try {
    // Find the NFT by mint address
    const nft = await mx.nfts().findByMint({
      mintAddress: new PublicKey(mintAddress)
    });
    
    console.log('✅ Found NFT:', nft.name);
    
    // Transfer the NFT
    const { response } = await mx.nfts().transfer({
      nftOrSft: nft,
      toOwner: new PublicKey(newOwnerAddress)
    });
    
    // Ensure NFT transfer signature is a base58 string
    const signature = response.signature;
    if (typeof signature !== "string") {
      throw new Error("NFT transfer signature must be a base58 string, got: " + typeof signature);
    }
    
    console.log("🔄 typeof NFT signature:", typeof signature, "value:", signature);
    console.log('✅ NFT transferred:', signature);
    
    const clusterConfig = getClusterConfig();
    
    return {
      signature: signature,
      explorerUrl: `${clusterConfig.explorerUrl}/tx/${signature}`,
      success: true
    };
  } catch (error) {
    console.error('❌ NFT transfer failed:', error);
    throw new Error(`NFT transfer failed: ${error.message}`);
  }
}

/**
 * Update piece ownership in database (legacy function)
 * @param {string} pieceId - Piece ID
 * @param {string} newOwnerWallet - New owner's wallet address
 * @param {string} transactionSignature - Blockchain transaction signature
 * @returns {Promise<Object>} Updated piece
 */
export async function updatePieceOwnership(pieceId, newOwnerWallet, transactionSignature) {
  try {
    console.log('💾 Updating piece ownership in database...');
    console.log('Piece ID:', pieceId);
    console.log('New owner:', newOwnerWallet);
    
    // Ensure transaction signature is a string
    const signatureString = typeof transactionSignature === 'string' 
      ? transactionSignature 
      : transactionSignature.toString();
    
    console.log('Transaction signature:', signatureString);
    
    // First, get the piece to find its mint_address and current holder (seller)
    const { data: existingPiece, error: fetchError } = await supabase
      .from('pieces')
      .select('id, mint_address, creator_id, listing_price, price, holder_wallet')
      .eq('id', pieceId)
      .maybeSingle();
    
    if (fetchError) {
      console.error('❌ Error fetching piece:', fetchError);
      throw new Error(`Could not find piece with ID: ${pieceId}`);
    }
    
    if (!existingPiece) {
      throw new Error(`No piece found with ID: ${pieceId}`);
    }
    
    const sellerWallet = existingPiece.holder_wallet; // Store seller wallet BEFORE update
    console.log('📋 Found piece:', existingPiece.mint_address);
    console.log('📝 Current holder (seller):', sellerWallet);
    
    // Update the piece with new owner using mint_address for better reliability
    const updateFilter = existingPiece.mint_address 
      ? { mint_address: existingPiece.mint_address }
      : { id: pieceId };
    
    const { data: updatedPieces, error: updateError } = await supabase
      .from('pieces')
      .update({
        holder_wallet: newOwnerWallet,
        listing_status: null,  // Clear listing status - buyer can list it fresh
        listing_price: null,  // Clear the listing price
        sold_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      })
      .match(updateFilter)
      .select()
      .maybeSingle();
    
    if (updateError) {
      console.error('❌ Error updating piece ownership:', updateError);
      throw updateError;
    }
    
    if (!updatedPieces) {
      const filterUsed = existingPiece.mint_address ? `mint_address=${existingPiece.mint_address}` : `id=${pieceId}`;
      throw new Error(`No rows updated using filter: ${filterUsed}`);
    }
    
    console.log('✅ Piece ownership updated in database');
    
    // Log the sale activity with the ORIGINAL holder (seller) as from_address
    if (existingPiece.mint_address) {
      const { error: activityError } = await supabase.from('nft_activity').insert({
        mint_address: existingPiece.mint_address,
        event_type: 'sale',
        from_address: sellerWallet, // Use the SELLER's wallet (original holder)
        to_address: newOwnerWallet,
        price: existingPiece.listing_price || existingPiece.price,
        transaction_hash: signatureString,
        created_at: new Date().toISOString()
      });
      
      if (activityError) {
        console.error('❌ Failed to log sale activity:', activityError);
      } else {
        console.log('✅ Sale activity logged -  from:', sellerWallet, 'to:', newOwnerWallet);
      }
    }
    
    return updatedPieces;
  } catch (error) {
    console.error('❌ Error in updatePieceOwnership:', error);
    throw error;
  }
}

/**
 * Update piece ownership in database using mint_address (atomic Auction House version)
 * @param {string} mintAddress - NFT mint address
 * @param {string} newOwnerWallet - New owner's wallet address
 * @param {string} transactionSignature - Blockchain transaction signature
 * @param {number} salePriceLamports - Sale price in lamports
 * @returns {Promise<Object>} Updated piece
 */
export async function updatePieceOwnershipByMint(mintAddress, newOwnerWallet, transactionSignature, salePriceLamports) {
  try {
    console.log('💾 Updating piece ownership in database (by mint_address)...');
    console.log('Mint Address:', mintAddress);
    console.log('New owner:', newOwnerWallet);
    console.log('Sale price (lamports):', salePriceLamports);
    
    // Ensure transaction signature is a string
    const signatureString = typeof transactionSignature === 'string' 
      ? transactionSignature 
      : transactionSignature.toString();
    
    console.log('Transaction signature:', signatureString);
    
    // FIRST: Fetch the piece to get the current holder_wallet (seller) BEFORE updating
    const { data: existingPiece, error: fetchError } = await supabase
      .from('pieces')
      .select('id, mint_address, holder_wallet, creator_id')
      .eq('mint_address', mintAddress)
      .maybeSingle();
    
    if (fetchError || !existingPiece) {
      console.error('❌ Error fetching piece before update:', fetchError);
      throw fetchError || new Error(`Piece not found with mint_address: ${mintAddress}`);
    }
    
    const sellerWallet = existingPiece.holder_wallet; // Store seller wallet BEFORE update
    console.log('📝 Current holder (seller):', sellerWallet);
    
    // Update the piece with new owner and sale information using mint_address
    const { data: updatedPieces, error: updateError } = await supabase
      .from('pieces')
      .update({
        holder_wallet: newOwnerWallet,
        listing_status: 'sold',  // Set to 'sold' after purchase - buyer must manually list to resell
        listing_price: null,  // Clear the listing price
        sold_at: new Date().toISOString(),
        last_sale_price_lamports: Number(salePriceLamports), // Ensure it's a proper number
        updated_at: new Date().toISOString()
      })
      .eq('mint_address', mintAddress) // Use mint_address instead of id
      .select()
      .maybeSingle();
    
    if (updateError) {
      console.error('❌ Error updating piece ownership:', updateError);
      throw updateError;
    }
    
    if (!updatedPieces) {
      throw new Error(`No rows updated using mint_address: ${mintAddress}`);
    }
    
    console.log('✅ Piece ownership updated in database');
    
    // Log the sale activity with the ORIGINAL holder (seller) as from_address
    if (mintAddress) {
      const now = new Date();
      const timestamp = Math.floor(now.getTime() / 1000);
      
      const { error: activityError } = await supabase.from('nft_activity').insert({
        mint_address: mintAddress,
        event_type: 'sale',
        from_address: sellerWallet, // Use the SELLER's wallet (original holder)
        to_address: newOwnerWallet,
        price: Number(salePriceLamports) / LAMPORTS_PER_SOL, // Convert lamports to SOL
        amount_lamports: Number(salePriceLamports),
        signature: signatureString,
        tx_sig: signatureString,
        transaction_hash: signatureString,
        block_time: now.toISOString(),
        created_at: now.toISOString(),
        event_timestamp: timestamp,
        source: 'app',
        slot: 0
      });
      
      if (activityError) {
        console.warn('⚠️ Failed to log sale activity (non-critical):', activityError.message);
        console.error('Activity error details:', activityError);
      } else {
        console.log('✅ Sale activity logged for', mintAddress, '- from:', sellerWallet, 'to:', newOwnerWallet);
      }
    }
    
    return updatedPieces;
  } catch (error) {
    console.error('❌ Error in updatePieceOwnershipByMint:', error);
    throw error;
  }
}

/**
 * Update piece ownership in database (atomic Auction House version)
 * @param {string} pieceId - Piece ID
 * @param {string} newOwnerWallet - New owner's wallet address
 * @param {string} transactionSignature - Blockchain transaction signature
 * @param {number} salePriceLamports - Sale price in lamports
 * @returns {Promise<Object>} Updated piece
 */
export async function updatePieceOwnershipAtomic(pieceId, newOwnerWallet, transactionSignature, salePriceLamports) {
  try {
    console.log('💾 Updating piece ownership in database (atomic)...');
    console.log('Piece ID:', pieceId);
    console.log('New owner:', newOwnerWallet);
    console.log('Sale price (lamports):', salePriceLamports);
    
    // Ensure transaction signature is a string
    const signatureString = typeof transactionSignature === 'string' 
      ? transactionSignature 
      : transactionSignature.toString();
    
    console.log('Transaction signature:', signatureString);
    
    // First, get the piece to find its mint_address and current holder (seller)
    const { data: existingPiece, error: fetchError } = await supabase
      .from('pieces')
      .select('id, mint_address, creator_id, listing_price, price, holder_wallet')
      .eq('id', pieceId)
      .maybeSingle();
    
    if (fetchError) {
      console.error('❌ Error fetching piece:', fetchError);
      throw new Error(`Could not find piece with ID: ${pieceId}`);
    }
    
    if (!existingPiece) {
      throw new Error(`No piece found with ID: ${pieceId}`);
    }
    
    const sellerWallet = existingPiece.holder_wallet; // Store seller wallet BEFORE update
    console.log('📋 Found piece:', existingPiece.mint_address);
    console.log('📝 Current holder (seller):', sellerWallet);
    
    // Update the piece with new owner and sale information using mint_address for better reliability
    const updateFilter = existingPiece.mint_address 
      ? { mint_address: existingPiece.mint_address }
      : { id: pieceId };
    
    const { data: updatedPieces, error: updateError } = await supabase
      .from('pieces')
      .update({
        holder_wallet: newOwnerWallet,
        listing_status: 'sold',  // Set to 'sold' after purchase - buyer must manually list to resell
        listing_price: null,  // Clear the listing price
        sold_at: new Date().toISOString(),
        last_sale_price_lamports: Number(salePriceLamports), // Ensure it's a proper number
        updated_at: new Date().toISOString()
      })
      .match(updateFilter)
      .select()
      .maybeSingle();
    
    if (updateError) {
      console.error('❌ Error updating piece ownership:', updateError);
      throw updateError;
    }
    
    if (!updatedPieces) {
      const filterUsed = existingPiece.mint_address ? `mint_address=${existingPiece.mint_address}` : `id=${pieceId}`;
      throw new Error(`No rows updated using filter: ${filterUsed}`);
    }
    
    console.log('✅ Piece ownership updated in database');
    
    // Log the sale activity with the ORIGINAL holder (seller) as from_address
    if (existingPiece.mint_address) {
      const now = new Date();
      const timestamp = Math.floor(now.getTime() / 1000);
      
      const { error: activityError } = await supabase.from('nft_activity').insert({
        mint_address: existingPiece.mint_address,
        event_type: 'sale',
        from_address: sellerWallet, // Use the SELLER's wallet (original holder)
        to_address: newOwnerWallet,
        price: Number(salePriceLamports) / LAMPORTS_PER_SOL, // Convert lamports to SOL
        amount_lamports: Number(salePriceLamports),
        signature: signatureString,
        tx_sig: signatureString,
        transaction_hash: signatureString,
        block_time: now.toISOString(),
        created_at: now.toISOString(),
        event_timestamp: timestamp,
        source: 'app',
        slot: 0
      });
      
      if (activityError) {
        console.warn('⚠️ Failed to log sale activity (non-critical):', activityError.message);
        console.error('Activity error details:', activityError);
      } else {
        console.log('✅ Sale activity logged for', existingPiece.mint_address, '- from:', sellerWallet, 'to:', newOwnerWallet);
      }
    }
    
    return updatedPieces;
  } catch (error) {
    console.error('❌ Error in updatePieceOwnershipAtomic:', error);
    throw error;
  }
}

/**
 * List a piece for sale on Auction House
 */
export async function listPieceForSaleOnAH(wallet, piece, price) {
  if (!wallet.connected || !wallet.publicKey) {
    throw new Error('Wallet not connected');
  }
  
  console.log('📝 Listing piece for sale on Auction House...');
  console.log('Piece:', piece.name);
  console.log('Price:', price, 'SOL');
  console.log('Mint Address:', piece.mint_address);
  
  try {
    // Validate wallet adapter
    validateWalletAdapter(wallet);
    
    // Check if piece is minted
    if (!piece.mint_address) {
      throw new Error('Piece is not minted and cannot be listed for sale');
    }
    
    // Create authorization for future transfers
    console.log('🔐 Creating transfer authorization...');
    const authInstruction = await createAuthorizedTransferInstruction({
      mintAddress: piece.mint_address,
      sellerWallet: wallet.publicKey.toString()
    });
    
    // For now, we'll store the authorization without requiring the seller to sign
    // In a full implementation, the seller would sign this transaction
    const authSignature = `AUTH_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    
    // Store authorization in database
    await storeAuthorization({
      mintAddress: piece.mint_address,
      sellerWallet: wallet.publicKey.toString(),
      authorizationSignature: authSignature,
      listingPrice: price
    });
    
    console.log('✅ Transfer authorization created and stored');
    
    // Try to list on Auction House first
    let listingResult;
    try {
      listingResult = await listForSale({
        mint: new PublicKey(piece.mint_address),
        priceSol: price,
        seller: wallet.publicKey
      });
      console.log('✅ Piece listed on Auction House:', listingResult.signature);
    } catch (ahError) {
      console.warn('⚠️ Auction House listing failed, using database-only listing:', ahError.message);
      // Create a mock listing signature for database tracking
      listingResult = {
        signature: `LISTING_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
      };
    }
    
    // Update database with listing information
    console.log('💾 Updating database...');
    const { data: updatedPiece, error: updateError } = await supabase
      .from('pieces')
      .update({
        listing_status: 'listed',
        listing_price: price,
        listed_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        authorization_signature: authSignature
      })
      .eq('mint_address', piece.mint_address)
      .select()
      .maybeSingle();
    
    if (updateError) {
      console.error('❌ Error updating piece listing:', updateError);
      throw updateError;
    }
    
    if (!updatedPiece) {
      throw new Error(`No rows updated using mint_address: ${piece.mint_address}`);
    }
    
    console.log('✅ Listing complete with authorization!');
    
    return {
      success: true,
      piece: updatedPiece,
      transactionSignature: listingResult.signature,
      authorizationSignature: authSignature,
      price: price
    };
    
  } catch (error) {
    console.error('❌ Listing failed:', error);
    throw error;
  }
}

/**
 * List a piece for sale on Auction House (legacy function)
 * @param {Object} wallet - Connected wallet adapter
 * @param {Object} piece - Piece object
 * @param {number} price - Price in SOL
 * @returns {Promise<Object>} Listing result
 */
export async function listPieceForSale(wallet, piece, price) {
  if (!wallet.connected || !wallet.publicKey) {
    throw new Error('Wallet not connected');
  }
  
  console.log('📝 Listing piece for sale on Auction House...');
  console.log('Piece:', piece.name);
  console.log('Price:', price, 'SOL');
  console.log('Mint Address:', piece.mint_address);
  
  try {
    // Validate wallet adapter
    validateWalletAdapter(wallet);
    
    // Check if piece is minted
    if (!piece.mint_address) {
      throw new Error('Piece is not minted and cannot be listed for sale');
    }
    
    // Try Auction House first, fall back to database-only listing if not available
    try {
      // Setup Metaplex and Auction House
      const connection = await getWorkingConnection();
      const mx = getMetaplex(connection, wallet);
      const auctionHouse = await getAuctionHouse(mx);
      
      // List the piece for sale
      const listingResult = await listForSale(
        mx,
        auctionHouse,
        new PublicKey(piece.mint_address),
        price,
        wallet.publicKey
      );
      
      console.log('✅ Piece listed for sale:', listingResult.signature);
      
      // Update database with listing information
      console.log('💾 Updating database...');
      
      // Use mint_address for filtering if available, otherwise use id
      const updateFilter = piece.mint_address 
        ? { mint_address: piece.mint_address }
        : { id: piece.id };
      
      const { data: updatedPiece, error: updateError } = await supabase
        .from('pieces')
        .update({
          listing_status: 'listed',
          listing_price: price,
          listed_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        })
        .match(updateFilter)
        .select()
        .maybeSingle();
      
      if (updateError) {
        console.error('❌ Error updating piece listing:', updateError);
        throw updateError;
      }
      
      if (!updatedPiece) {
        const filterUsed = piece.mint_address ? `mint_address=${piece.mint_address}` : `id=${piece.id}`;
        throw new Error(`No rows updated using filter: ${filterUsed}`);
      }
      
      console.log('✅ Listing complete!');
      
      // Create nft_activity entry for the list event
      try {
        console.log('📝 Creating nft_activity entry for listing...');
        
        const now = new Date();
        const timestamp = Math.floor(now.getTime() / 1000);
        
        const activityData = {
          mint_address: piece.mint_address,
          event_type: 'list',
          from_address: wallet.publicKey.toString(),
          to_address: null,
          price: price,
          amount_lamports: Math.floor(price * LAMPORTS_PER_SOL),
          event_timestamp: timestamp,
          block_time: now.toISOString(),
          created_at: now.toISOString(),
          slot: 0,
          signature: listingResult.signature || 'app-list-' + Date.now(),
          tx_sig: listingResult.signature || 'app-list-' + Date.now(),
          source: 'app'
        };

        const { error: activityError } = await supabase
          .from('nft_activity')
          .insert(activityData);

        if (activityError) {
          console.warn('⚠️ Failed to create nft_activity entry (non-critical):', activityError.message);
        } else {
          console.log('✅ nft_activity entry created successfully');
        }
      } catch (activityError) {
        console.warn('⚠️ Activity logging failed (non-critical):', activityError);
      }
      
      return {
        success: true,
        piece: updatedPiece,
        transactionSignature: listingResult.signature,
        price: price
      };
      
    } catch (auctionHouseError) {
      console.warn('⚠️ Auction House not available, using database-only listing:', auctionHouseError.message);
      
      // Fall back to database-only listing
      console.log('💾 Updating database listing...');
      
      // Use mint_address for filtering if available, otherwise use id
      const updateFilter = piece.mint_address 
        ? { mint_address: piece.mint_address }
        : { id: piece.id };
      
      const { data: updatedPiece, error: updateError } = await supabase
        .from('pieces')
        .update({
          listing_status: 'listed',
          listing_price: price,
          listed_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        })
        .match(updateFilter)
        .select()
        .maybeSingle();
      
      if (updateError) {
        console.error('❌ Error updating piece listing:', updateError);
        throw updateError;
      }
      
      if (!updatedPiece) {
        const filterUsed = piece.mint_address ? `mint_address=${piece.mint_address}` : `id=${piece.id}`;
        throw new Error(`No rows updated using filter: ${filterUsed}`);
      }
      
      console.log('✅ Database listing complete!');
      
      // Create nft_activity entry for the list event
      try {
        console.log('📝 Creating nft_activity entry for listing...');
        
        const activityData = {
          mint_address: piece.mint_address,
          event_type: 'list',
          from_address: wallet.publicKey.toString(),
          amount_lamports: Math.floor(price * LAMPORTS_PER_SOL),
          event_timestamp: Date.now(),
          slot: 0,
          signature: 'local-list-' + Date.now(),
          source: 'app'
        };

        const { error: activityError } = await supabase
          .from('nft_activity')
          .insert(activityData);

        if (activityError) {
          console.warn('⚠️ Failed to create nft_activity entry (non-critical):', activityError.message);
        } else {
          console.log('✅ nft_activity entry created successfully');
        }
      } catch (activityError) {
        console.warn('⚠️ Activity logging failed (non-critical):', activityError);
      }
      
      return {
        success: true,
        piece: updatedPiece,
        transactionSignature: null, // No blockchain transaction
        price: price
      };
    }
    
  } catch (error) {
    console.error('❌ Listing failed:', error);
    throw error;
  }
}

/**
 * Complete purchase flow: Transfer SOL + Transfer NFT (if minted) + Update database
 * @param {Object} wallet - Connected wallet adapter
 * @param {Object} piece - Piece object with seller info
 * @param {number} price - Price in SOL
 * @returns {Promise<Object>} Complete purchase result
 */
export async function completePurchase(wallet, piece, price) {
  if (!wallet.connected || !wallet.publicKey) {
    throw new Error('Wallet not connected');
  }
  
  console.log('🛒 Starting atomic Auction House purchase...');
  console.log('Piece:', piece.name);
  console.log('Price:', price, 'SOL');
  console.log('Mint Address:', piece.mint_address);
  console.log('Piece ID:', piece.id);
  
  try {
    // Validate wallet adapter
    validateWalletAdapter(wallet);
    
    // Get seller's wallet address
    const sellerAddress = piece.holder_wallet || piece.creators?.wallet || piece.creator?.wallet;
    
    if (!sellerAddress) {
      throw new Error('Seller wallet address not found');
    }
    
    // Check if buyer is trying to buy their own piece
    if (sellerAddress === wallet.publicKey.toString()) {
      throw new Error('You cannot buy your own piece');
    }
    
    // Check if piece is minted
    if (!piece.mint_address) {
      throw new Error('Piece is not minted and cannot be purchased');
    }
    
    // Verify piece exists in database first
    console.log('🔍 Verifying piece exists in database...');
    const { data: existingPiece, error: fetchError } = await supabase
      .from('pieces')
      .select('id, mint_address, holder_wallet, listing_status')
      .eq('mint_address', piece.mint_address)
      .maybeSingle();
    
    if (fetchError || !existingPiece) {
      console.error('❌ Piece not found in database:', fetchError);
      throw new Error(`Piece not found: ${piece.mint_address}`);
    }
    
    console.log('✅ Piece verified in database:', {
      id: existingPiece.id,
      mint_address: existingPiece.mint_address,
      holder_wallet: existingPiece.holder_wallet,
      listing_status: existingPiece.listing_status
    });
    
    // Try Auction House first, fall back to manual approach if not available
    let purchaseResult;
    let isAtomic = false;
    
    try {
      console.log('💰 Attempting atomic Auction House purchase...');
      purchaseResult = await buyNow({
        mint: new PublicKey(piece.mint_address),
        priceSol: price,
        buyer: wallet.publicKey,
        seller: new PublicKey(sellerAddress)
      });
      
      console.log('✅ Atomic purchase successful:', purchaseResult.txSig);
      isAtomic = true;
      
    } catch (auctionHouseError) {
      console.warn('⚠️ Auction House not available, using authorized transfer approach:', auctionHouseError.message);
      
      // Check if the piece has authorization for transfers
      console.log('🔐 Checking for transfer authorization...');
      const authCheck = await checkAuthorization(piece.mint_address);
      
      if (!authCheck.hasAuthorization) {
        console.log('ℹ️ No authorization found, but proceeding with fallback transfer method');
        // Don't throw error - use fallback method instead
      } else {
        console.log('✅ Transfer authorization found');
      }
      
      // PRE-VALIDATE before sending money
      console.log('🔍 Pre-validating transfer...');
      try {
        if (!piece.mint_address || !sellerAddress) {
          throw new Error('Invalid piece data');
        }
        
        // Verify piece is still available
        const { data: dbPiece, error: dbError } = await supabase
          .from('pieces')
          .select('listing_status, is_delegate_active')
          .eq('mint_address', piece.mint_address)
          .maybeSingle();
        
        if (dbError || !dbPiece) {
          throw new Error('Piece not found - may have been sold');
        }
        
        if (dbPiece.listing_status !== 'listed') {
          throw new Error(`Piece is not available (status: ${dbPiece.listing_status})`);
        }
        
        console.log('✅ Pre-validation passed');
      } catch (preValidateError) {
        throw new Error(`Cannot proceed: ${preValidateError.message}`);
      }
      
      // FINAL availability check RIGHT BEFORE payment (legacy purchase)
      console.log('🔒 Final availability check before charging...');
      const { data: finalCheck, error: finalCheckError } = await supabase
        .from('pieces')
        .select('listing_status, holder_wallet')
        .eq('mint_address', piece.mint_address)
        .eq('holder_wallet', sellerAddress)
        .eq('listing_status', 'listed')
        .maybeSingle();
      
      if (finalCheckError || !finalCheck) {
        throw new Error('⚠️ This piece was just purchased by another buyer. No payment was made. Please refresh the page to see available pieces.');
      }
      
      console.log('✅ Final check passed - proceeding with payment');
      
      // Execute SOL payment (only after validation!)
      console.log('💰 Executing SOL payment...');
      let paymentResult;
      try {
        paymentResult = await executeSaleTransaction(wallet, sellerAddress, price);
        console.log('✅ SOL payment successful:', paymentResult.signature);
      } catch (paymentError) {
        console.error('❌ Payment failed:', paymentError);
        throw new Error(`Payment failed: ${paymentError.message}`);
      }
      
      // Execute NFT transfer using delegate
      console.log('🎨 Requesting NFT transfer via delegate...');
      let transferResult;
      try {
        transferResult = await requestDelegateTransfer({
          piece,
          sellerWallet: sellerAddress,
          buyerWallet: wallet.publicKey.toString(),
          paymentSignature: paymentResult.signature,
          priceLamports: Math.floor(price * LAMPORTS_PER_SOL),
          wallet: wallet // Buyer wallet for signing gas fee transaction
        });
        
        console.log('✅ NFT transfer completed:', transferResult.transferSignature);
        
        // Database is automatically updated by the edge function
        console.log('✅ Database updated by edge function');
      } catch (transferError) {
        console.error('❌ NFT transfer failed after payment:', transferError);
        console.error('💰 Payment signature:', paymentResult.signature);
        
        // Log the failed purchase for support
        try {
          const now = new Date();
          const timestamp = Math.floor(now.getTime() / 1000);
          const priceLamports = Math.floor(price * LAMPORTS_PER_SOL);
          
          await supabase.from('nft_activity').insert({
            mint_address: piece.mint_address,
            event_type: 'sale', // Use 'sale' since 'failed_purchase' isn't in schema
            from_address: sellerAddress,
            to_address: wallet.publicKey.toString(),
            price: price,
            amount_lamports: priceLamports,
            signature: paymentResult.signature,
            tx_sig: paymentResult.signature,
            event_timestamp: timestamp,
            block_time: now.toISOString(),
            created_at: now.toISOString(),
            source: 'app',
            slot: 0,
            raw: { status: 'payment_succeeded_transfer_failed' }
          });
        } catch (logError) {
          console.error('Failed to log error:', logError);
        }
        
        throw new Error(
          `Payment sent (${paymentResult.signature.slice(0, 8)}...) but transfer failed: ${transferError.message}. ` +
          `Contact support with TX: ${paymentResult.signature}`
        );
      }
      
      purchaseResult = {
        txSig: paymentResult.signature,
        transferSignature: transferResult.transferSignature,
        fallback: false,
        authorized: true
      };
      isAtomic = false;
    }
    
    // Database update is handled by the server-side transfer function
    let updatedPiece = piece; // Use original piece data
    
    if (isAtomic) {
      console.log('✅ Purchase complete! NFT and SOL transferred atomically.');
    } else {
      console.log('✅ Purchase complete! SOL payment successful, NFT transfer handled separately.');
    }
    
    return {
      success: true,
      piece: updatedPiece,
      transactionSignature: purchaseResult.txSig,
      price: price,
      priceLamports: Math.floor(price * LAMPORTS_PER_SOL),
      atomic: isAtomic,
      fallback: purchaseResult.fallback || false
    };
    
  } catch (error) {
    console.error('❌ Purchase failed:', error);
    console.error('❌ Error details:', {
      message: error.message,
      stack: error.stack,
      name: error.name
    });
    throw error;
  }
}

/**
 * Check if wallet has sufficient balance for purchase
 * @param {Object} wallet - Connected wallet adapter
 * @param {number} price - Price in SOL
 * @returns {Promise<Object>} Balance check result
 */
export async function checkSufficientBalance(wallet, price) {
  if (!wallet.connected || !wallet.publicKey) {
    return {
      sufficient: false,
      error: 'Wallet not connected'
    };
  }
  
  try {
    const connection = await getWorkingConnection();
    const balance = await connection.getBalance(wallet.publicKey);
    const balanceSOL = balance / LAMPORTS_PER_SOL;
    
    // Add 0.01 SOL buffer for transaction fees
    const requiredBalance = price + 0.01;
    
    return {
      sufficient: balanceSOL >= requiredBalance,
      balance: balanceSOL,
      required: requiredBalance,
      shortfall: Math.max(0, requiredBalance - balanceSOL)
    };
  } catch (error) {
    console.error('Error checking balance:', error);
    return {
      sufficient: false,
      error: error.message
    };
  }
}

/**
 * List piece for sale (update database only - legacy function)
 * @param {string} pieceId - Piece ID
 * @param {number} price - Listing price in SOL
 * @returns {Promise<Object>} Updated piece
 */
export async function listPieceForSaleLegacy(pieceId, price) {
  try {
    console.log('📝 Listing piece for sale (legacy)...');
    console.log('Piece ID:', pieceId);
    console.log('Price:', price, 'SOL');
    
    const { data, error } = await supabase
      .from('pieces')
      .update({
        listing_status: 'listed',
        listing_price: price,
        price: price,
        listed_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      })
      .eq('id', pieceId)
      .select()
      .maybeSingle();
    
    if (error) {
      console.error('❌ Error listing piece:', error);
      throw error;
    }
    
    console.log('✅ Piece listed for sale');
    return data;
  } catch (error) {
    console.error('❌ Error in listPieceForSaleLegacy:', error);
    throw error;
  }
}

/**
 * Cancel piece listing
 * @param {string} pieceId - Piece ID
 * @returns {Promise<Object>} Updated piece
 */
export async function cancelListing(pieceId) {
  try {
    console.log('❌ Cancelling listing...');
    console.log('Piece ID:', pieceId);
    
    const { data, error } = await supabase
      .from('pieces')
      .update({
        listing_status: 'cancelled',
        updated_at: new Date().toISOString()
      })
      .eq('id', pieceId)
      .select()
      .maybeSingle();
    
    if (error) {
      console.error('❌ Error cancelling listing:', error);
      throw error;
    }
    
    console.log('✅ Listing cancelled');
    return data;
  } catch (error) {
    console.error('❌ Error in cancelListing:', error);
    throw error;
  }
}

/**
 * List piece with seller pre-authorization (NEW DELEGATE SYSTEM)
 * This is the recommended way to list NFTs for sale
 * @param {Object} wallet - Connected wallet adapter
 * @param {Object} piece - Piece object
 * @param {number} price - Price in SOL
 * @returns {Promise<Object>} Listing result
 */
export async function listPieceWithDelegate(wallet, piece, price) {
  if (!wallet.connected || !wallet.publicKey) {
    throw new Error('Wallet not connected');
  }

  console.log('📝 Listing piece with seller pre-authorization...');
  console.log('Piece:', piece.name);
  console.log('Price:', price, 'SOL');

  try {
    const connection = await getWorkingConnection();

    // Use the new delegate auth system
    const result = await listPieceWithDelegateAuth({
      wallet,
      piece,
      priceSol: price,
      connection
    });

    return {
      success: true,
      piece: result.piece,
      transactionSignature: result.authSignature,
      price: price,
      delegateActive: true
    };

  } catch (error) {
    console.error('❌ Listing with delegate failed:', error);
    throw error;
  }
}

/**
 * Cancel listing with delegate revocation (NEW DELEGATE SYSTEM)
 * @param {Object} wallet - Connected wallet adapter  
 * @param {Object} piece - Piece object
 * @returns {Promise<Object>} Cancel result
 */
export async function cancelListingWithDelegate(wallet, piece) {
  if (!wallet.connected || !wallet.publicKey) {
    throw new Error('Wallet not connected');
  }

  console.log('❌ Cancelling listing with delegate revocation...');

  try {
    const connection = await getWorkingConnection();

    const result = await cancelListingWithRevoke({
      wallet,
      piece,
      connection
    });

    return {
      success: true,
      transactionSignature: result.revokeSignature
    };

  } catch (error) {
    console.error('❌ Cancel listing with delegate failed:', error);
    throw error;
  }
}

/**
 * Complete purchase using delegate transfer (NEW DELEGATE SYSTEM)
 * This is the recommended way to purchase NFTs
 * @param {Object} wallet - Connected wallet adapter (buyer)
 * @param {Object} piece - Piece object
 * @param {number} price - Price in SOL
 * @returns {Promise<Object>} Purchase result
 */
export async function completePurchaseWithDelegate(wallet, piece, price) {
  if (!wallet.connected || !wallet.publicKey) {
    throw new Error('Wallet not connected');
  }

  // Track performance
  const startTime = Date.now();
  const operationId = `purchase-${piece.mint_address}-${Date.now()}`;

  console.log('🛒 Starting purchase with delegate transfer...');
  console.log('Piece:', piece.name);
  console.log('Price:', price, 'SOL');

  try {
    // FAST PRE-QUEUE CHECK: Fail immediately if piece is already sold
    // This prevents users from waiting in queue just to see an error
    console.log('⚡ Quick availability check before queuing...');
    const { data: quickCheck, error: quickCheckError } = await supabase
      .from('pieces')
      .select('listing_status, holder_wallet')
      .eq('mint_address', piece.mint_address)
      .maybeSingle();
    
    if (quickCheckError || !quickCheck) {
      throw new Error('This piece is no longer available.');
    }
    
    if (quickCheck.listing_status !== 'listed') {
      throw new Error(`This piece is ${quickCheck.listing_status === 'sold' ? 'already sold' : 'no longer available'}. Please refresh the page.`);
    }
    
    console.log('✅ Quick check passed - adding to purchase queue');
    
    // Wrap in deduplication and queue
    // CRITICAL: Set maxConcurrencyForType = 1 to serialize purchases
    // This prevents race conditions where multiple buyers pass pre-payment check simultaneously
    const result = await deduplicateTransaction(
      'purchase',
      () => queueTransaction(
        () => executePurchaseInternal(wallet, piece, price),
        {
          priority: 1,
          timeout: 120000, // 2 minutes
          retries: 2,
          id: operationId,
          operationType: 'purchase', // NEW: Identify this as a purchase operation
          maxConcurrencyForType: 1 // NEW: Only 1 purchase can process at a time (prevents race conditions)
        }
      ),
      { 
        mintAddress: piece.mint_address, 
        price, 
        buyer: wallet.publicKey.toString() 
      }
    );

    // Track success
    trackTransaction('purchase', startTime, true);
    return result;

  } catch (error) {
    // Track failure
    trackTransaction('purchase', startTime, false, error.message);
    throw error;
  }
}

/**
 * Internal purchase execution (wrapped by queue and deduplication)
 */
async function executePurchaseInternal(wallet, piece, price) {
  console.log('⚙️ Processing queued purchase transaction...');

  try {
    // Use circuit breaker for RPC connection
    const connection = await executeWithRpcCircuitBreaker(() => getWorkingConnection());

    // Step 1: Get seller address
    // Try multiple fields: holder_wallet (current owner), creator data, or fetch from DB
    let sellerAddress = piece.holder_wallet || 
                        piece.creator_wallet || 
                        piece.creators?.wallet || 
                        piece.creator?.wallet ||
                        piece.creators?.[0]?.wallet;
    
    // If still no seller address, fetch from database
    if (!sellerAddress && piece.mint_address) {
      console.log('🔍 No seller address in piece object, fetching from database...');
      const { data: dbPiece, error: dbError } = await supabase
        .from('pieces')
        .select('holder_wallet, creator_id, creators!inner(wallet)')
        .eq('mint_address', piece.mint_address)
        .maybeSingle();
      
      if (!dbError && dbPiece) {
        sellerAddress = dbPiece.holder_wallet || dbPiece.creators?.wallet;
        console.log('✅ Found seller address from database:', sellerAddress);
      }
    }
    
    if (!sellerAddress) {
      console.error('❌ No seller address found in piece:', piece);
      throw new Error('Seller wallet address not found. Please try refreshing the page.');
    }
    
    console.log('✅ Using seller address:', sellerAddress);

    // Step 2: Verify buyer isn't seller
    if (sellerAddress === wallet.publicKey.toString()) {
      throw new Error('You cannot buy your own piece');
    }

    // Step 3: Verify piece is minted
    if (!piece.mint_address) {
      throw new Error('Piece is not minted and cannot be purchased');
    }

    // Step 4: Verify delegate authorization is active (on-chain check)
    console.log('🔍 Verifying delegate authorization on-chain...');
    const delegateCheck = await verifyDelegateBeforePurchase({
      connection,
      mintAddress: piece.mint_address,
      sellerPubkeyBase58: sellerAddress,
      platformDelegateBase58: PLATFORM_WALLET_ADDRESS,
    });

    if (!delegateCheck.ok) {
      throw new Error(
        delegateCheck.error || 
        'Delegate authorization is not active. The seller may need to re-list this item.'
      );
    }

    console.log('✅ Delegate authorization verified on-chain');
    if (delegateCheck.program) {
      console.log(`📋 Token program: ${delegateCheck.program}, ATA: ${delegateCheck.sellerAta}`);
    }

    // Step 5: Get creator info for royalty payment
    console.log('💰 Fetching creator and royalty info...');
    const creatorWallet = piece.creator_wallet || piece.creators?.wallet;
    const royaltyBps = piece.royalty_bps || 0;
    
    if (royaltyBps > 0 && creatorWallet) {
      console.log(`   Creator: ${creatorWallet}`);
      console.log(`   Royalty: ${royaltyBps} bps (${(royaltyBps / 100).toFixed(1)}%)`);
    }

    // Step 6: PRE-VALIDATE the transfer will work (before sending money!)
    console.log('🔍 Pre-validating transfer will succeed...');
    try {
      // Verify the edge function is accessible and piece data is valid
      if (!piece.mint_address) {
        throw new Error('Invalid piece: missing mint_address');
      }
      if (!sellerAddress) {
        throw new Error('Invalid piece: missing seller address');
      }
      // CRITICAL: Atomic pre-validation check to prevent double purchases
      // Verify seller still owns AND piece is still listed in a single query
      const { data: dbPiece, error: dbError } = await supabase
        .from('pieces')
        .select('holder_wallet, listing_status, is_delegate_active, updated_at')
        .eq('mint_address', piece.mint_address)
        .eq('holder_wallet', sellerAddress)  // Verify seller STILL owns it
        .eq('listing_status', 'listed')  // Verify STILL listed
        .maybeSingle();
      
      if (dbError) {
        throw new Error(`Database error checking piece availability: ${dbError.message}`);
      }
      
      if (!dbPiece) {
        // This means either: piece doesn't exist, seller changed, or it's not listed anymore
        throw new Error('This piece is no longer available - it may have just been sold to another buyer. Please refresh and try another piece.');
      }
      
      if (dbPiece.is_delegate_active === false) {
        throw new Error('Seller authorization has expired. The seller needs to re-list this piece.');
      }
      
      console.log('✅ Pre-validation passed - piece is available and ready for purchase');
      console.log('   Seller:', dbPiece.holder_wallet?.slice(0, 8), '| Status:', dbPiece.listing_status);
    } catch (preValidateError) {
      console.error('❌ Pre-validation failed:', preValidateError.message);
      throw new Error(`Cannot proceed with purchase: ${preValidateError.message}`);
    }

    // Step 7: FINAL availability check RIGHT BEFORE payment
    // This prevents charging the second buyer if piece was sold after pre-validation
    console.log('🔒 Final availability check before charging...');
    const { data: finalCheck, error: finalCheckError } = await supabase
      .from('pieces')
      .select('listing_status, holder_wallet')
      .eq('mint_address', piece.mint_address)
      .eq('holder_wallet', sellerAddress)
      .eq('listing_status', 'listed')
      .maybeSingle();
    
    if (finalCheckError || !finalCheck) {
      throw new Error('⚠️ This piece was just purchased by another buyer. No payment was made. Please refresh the page to see available pieces.');
    }
    
    console.log('✅ Final check passed - proceeding with payment');
    
    // Step 8: Execute SOL payment (including royalties)
    // CRITICAL: This must only happen AFTER final availability check
    console.log('💰 Processing payment...');
    let paymentResult;
    try {
      paymentResult = await executeSaleTransaction(
        wallet, 
        sellerAddress, 
        price,
        creatorWallet,
        royaltyBps
      );
      console.log('✅ Payment successful:', paymentResult.signature);
    } catch (paymentError) {
      console.error('❌ Payment failed:', paymentError);
      throw new Error(`Payment transaction failed: ${paymentError.message}`);
    }

    // Step 9: Request server-side delegate transfer
    // If this fails, we need to handle it gracefully
    console.log('🎨 Requesting NFT transfer via delegate...');
    let transferResult;
    try {
      transferResult = await requestDelegateTransfer({
        piece,
        sellerWallet: sellerAddress,
        buyerWallet: wallet.publicKey.toString(),
        paymentSignature: paymentResult.signature,
        priceLamports: Math.floor(price * LAMPORTS_PER_SOL),
        wallet: wallet // Buyer wallet for signing gas fee transaction
      });

      console.log('✅ NFT transfer completed:', transferResult.transferSignature);
    } catch (transferError) {
      console.error('❌ NFT transfer failed after payment:', transferError);
      console.error('💰 IMPORTANT: Payment was sent but transfer failed');
      console.error('📋 Payment signature:', paymentResult.signature);
      console.error('📋 Please contact support with this transaction signature for assistance');
      
      // Log to database for support tracking
      try {
        const now = new Date();
        const timestamp = Math.floor(now.getTime() / 1000);
        const priceLamports = Math.floor(price * LAMPORTS_PER_SOL);
        
        await supabase.from('nft_activity').insert({
          mint_address: piece.mint_address,
          event_type: 'sale', // Use 'sale' since 'failed_purchase' isn't in schema
          from_address: sellerAddress,
          to_address: wallet.publicKey.toString(),
          price: price,
          amount_lamports: priceLamports,
          signature: paymentResult.signature,
          tx_sig: paymentResult.signature,
          event_timestamp: timestamp,
          block_time: now.toISOString(),
          created_at: now.toISOString(),
          source: 'app',
          slot: 0,
          raw: { status: 'payment_succeeded_transfer_failed' } // Store failure status in raw field
        });
      } catch (logError) {
        console.error('Failed to log error to database:', logError);
      }
      
      throw new Error(
        `Payment was sent (${paymentResult.signature.slice(0, 8)}...) but NFT transfer failed: ${transferError.message}. ` +
        `Please contact support with transaction signature: ${paymentResult.signature}`
      );
    }

    // Calculate fee breakdown including royalties
    const feeBreakdown = calculateSaleFee(price, null, royaltyBps);

    // Create nft_activity entry for the sale event (SAME LOGIC AS MINT TRACKING)
    // This is CRITICAL - log it the same way mints work
    try {
      console.log('📝 [SALE ACTIVITY] Creating nft_activity entry for sale...');
      console.log('📝 [SALE ACTIVITY] Mint address:', piece.mint_address);
      console.log('📝 [SALE ACTIVITY] From (seller):', sellerAddress);
      console.log('📝 [SALE ACTIVITY] To (buyer):', wallet.publicKey.toString());
      console.log('📝 [SALE ACTIVITY] Price:', price, 'SOL');
      
      // Check if sale activity already exists to prevent duplicates (like mint tracking)
      const { data: existingSaleActivity, error: checkError } = await supabase
        .from('nft_activity')
        .select('id')
        .eq('mint_address', piece.mint_address)
        .eq('event_type', 'sale')
        .eq('to_address', wallet.publicKey.toString())
        .gte('created_at', new Date(Date.now() - 60000).toISOString()) // Last 60 seconds
        .maybeSingle();

      if (checkError) {
        console.warn('⚠️ [SALE ACTIVITY] Error checking for existing activity:', checkError.message);
      }

      if (existingSaleActivity) {
        console.log('ℹ️ [SALE ACTIVITY] Sale activity already exists for this purchase, skipping duplicate');
      } else {
        const now = new Date();
        const timestamp = Math.floor(now.getTime() / 1000); // Unix timestamp in seconds
        const priceLamports = Math.floor(price * LAMPORTS_PER_SOL);
        const tempSignature = paymentResult.signature || ('app-sale-' + piece.mint_address + '-' + timestamp);
        
        const activityData = {
          mint_address: piece.mint_address,
          event_type: 'sale',
          from_address: sellerAddress,
          to_address: wallet.publicKey.toString(),
          price: price, // Price in SOL
          amount_lamports: priceLamports,
          event_timestamp: timestamp, // Unix timestamp in seconds
          block_time: now.toISOString(),
          created_at: now.toISOString(),
          slot: 0,
          signature: tempSignature,
          tx_sig: tempSignature,
          source: 'app'
        };

        console.log('📝 [SALE ACTIVITY] Inserting activity data:', activityData);

        const { data: insertedActivity, error: activityError } = await supabase
          .from('nft_activity')
          .insert(activityData)
          .select();

        if (activityError) {
          console.error('❌ [SALE ACTIVITY] Failed to create nft_activity entry:', activityError.message);
          console.error('❌ [SALE ACTIVITY] Error details:', activityError);
          console.error('❌ [SALE ACTIVITY] Activity data attempted:', activityData);
        } else {
          console.log('✅ [SALE ACTIVITY] nft_activity entry created successfully!');
          console.log('✅ [SALE ACTIVITY] Inserted activity:', insertedActivity);
        }
      }
    } catch (activityError) {
      console.error('❌ [SALE ACTIVITY] Activity logging failed (non-critical):', activityError);
    }

    return {
      success: true,
      piece: piece,
      paymentTransaction: {
        signature: paymentResult.signature,
        explorerUrl: paymentResult.explorerUrl
      },
      nftTransaction: {
        signature: transferResult.transferSignature,
        explorerUrl: `https://explorer.solana.com/tx/${transferResult.transferSignature}`
      },
      feeBreakdown: feeBreakdown,
      price: price,
      priceLamports: Math.floor(price * LAMPORTS_PER_SOL),
      delegateTransfer: true
    };

  } catch (error) {
    console.error('❌ Purchase with delegate failed:', error);
    throw error;
  }
}

/**
 * Fetch all NFTs owned by a wallet from the blockchain
 * @param {Object} wallet - Connected wallet adapter
 * @returns {Promise<Array>} Array of NFT objects from the wallet
 */
export async function fetchWalletNFTs(wallet) {
  if (!wallet?.publicKey) {
    console.warn('⚠️ No wallet connected');
    return [];
  }

  try {
    console.log('🔍 Fetching NFTs from wallet:', wallet.publicKey.toString());
    
    const connection = await getWorkingConnection();
    console.log('✅ Got connection:', connection.rpcEndpoint);
    
    // Initialize Metaplex with both connection and wallet
    const mx = getMetaplex(connection, wallet);
    console.log('✅ Initialized Metaplex');
    
    // Find all NFTs owned by this wallet
    console.log('🔍 Searching for NFTs owned by:', wallet.publicKey.toString());
    const nfts = await mx.nfts().findAllByOwner({
      owner: wallet.publicKey
    });
    
    console.log(`✅ Found ${nfts.length} NFTs in wallet`);
    
    if (nfts.length === 0) {
      console.log('ℹ️ No NFTs found in this wallet');
      return [];
    }
    
    // Load full metadata for each NFT (including JSON)
    console.log('📥 Loading full metadata for NFTs...');
    const nftsWithMetadata = await Promise.all(
      nfts.map(async (nft, index) => {
        try {
          // Load full NFT data if it's lazy-loaded
          const fullNft = nft.lazy ? await mx.nfts().load({ metadata: nft }) : nft;
          
          console.log(`  📦 NFT ${index + 1}: ${fullNft.name} (${fullNft.address.toString()})`);
          console.log(`     - URI: ${fullNft.uri}`);
          console.log(`     - Has JSON: ${!!fullNft.json}`);
          console.log(`     - Image: ${fullNft.json?.image || 'No image'}`);
          console.log(`     - Animation: ${fullNft.json?.animation_url || 'No animation'}`);
          
          // If json is empty, try fetching it from the URI
          let metadata = fullNft.json;
          if (!metadata || (!metadata.image && !metadata.animation_url)) {
            try {
              console.log(`     - Fetching metadata from URI...`);
              const response = await fetch(fullNft.uri);
              if (response.ok) {
                metadata = await response.json();
                console.log(`     ✅ Fetched metadata from URI:`, metadata);
              }
            } catch (fetchError) {
              console.warn(`     ⚠️ Could not fetch metadata from URI:`, fetchError.message);
            }
          }
          
          return {
            mintAddress: fullNft.address.toString(),
            name: fullNft.name,
            symbol: fullNft.symbol,
            uri: fullNft.uri,
            sellerFeeBasisPoints: fullNft.sellerFeeBasisPoints,
            creators: fullNft.creators,
            updateAuthority: fullNft.updateAuthorityAddress?.toString(),
            // Add metadata (fetched or original)
            json: metadata || fullNft.json,
            // For compatibility
            model: fullNft.model,
            lazy: false
          };
        } catch (loadError) {
          console.warn(`⚠️ Could not load full metadata for NFT ${index + 1}:`, loadError.message);
          // Return basic info if loading fails
          return {
            mintAddress: nft.address.toString(),
            name: nft.name,
            symbol: nft.symbol,
            uri: nft.uri,
            sellerFeeBasisPoints: nft.sellerFeeBasisPoints,
            creators: nft.creators,
            updateAuthority: nft.updateAuthorityAddress?.toString(),
            json: nft.json,
            model: nft.model,
            lazy: nft.lazy
          };
        }
      })
    );
    
    return nftsWithMetadata;
    
  } catch (error) {
    console.error('❌ Error fetching wallet NFTs:', error);
    console.error('Error details:', error.message, error.stack);
    return [];
  }
}

// Export the new delegate functions
export {
  listPieceWithDelegateAuth,
  cancelListingWithRevoke,
  verifyDelegateBeforePurchase,
  requestDelegateTransfer,
  needsReauthorization
};

export default {
  calculateSaleFee,
  executeSaleTransaction,
  transferNFTOwnership,
  updatePieceOwnership,
  completePurchase,
  checkSufficientBalance,
  listPieceForSale,
  cancelListing,
  PLATFORM_FEE_PERCENTAGE,
  PLATFORM_WALLET_ADDRESS
};

